import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle, ChevronRight, Images, Loader2, Plus, Search, Trash2, X } from "lucide-react";

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}
import { normalizeFuelType, normalizeTransmission } from "../../lib/auction-utils";

// ── Types ─────────────────────────────────────────────────────────────────────

type LookedUpVehicle = {
  registrationNumber: string;
  vin: string | null;
  make: string;
  model: string;
  modelYear: number | null;
  fuelType: string | null;
  transmission: string | null;
  wheelDrive: string | null;
  bodyType: string | null;
  exteriorColor: string | null;
  registrationDate: string | null;
  nextEuControl: string | null;
};

type ImageRow = { url: string; localPreview?: string; uploading?: boolean; error?: string };
type ConditionReport = {
  id: string;
  fileName?: string | null;
  uploading?: boolean;
  error?: string;
};
type ShippingAddress = {
  label?: string;
  address: string;
  postalCode: string;
  city: string;
  contactPhone?: string;
};

type OrganizationDetail = {
  shippingAddresses?: ShippingAddress[] | null;
};

const STEPS = ["Bil", "Kilometerstand", "Bilder", "Detaljer", "Hentested", "Auksjonstype"] as const;

async function readApiError(
  response: Response,
  fallbackMessage = "Noe uventet skjedde. Prøv igjen om et lite øyeblikk.",
) {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    const data = (await response.json().catch(() => null)) as { error?: unknown; message?: unknown } | null;
    const message =
      typeof data?.error === "string"
        ? data.error
        : typeof data?.message === "string"
          ? data.message
          : null;

    return message ?? fallbackMessage;
  }

  const text = (await response.text().catch(() => ""))?.trim();
  if (!text) {
    return fallbackMessage;
  }

  if (text.toLowerCase().includes("internal server error")) {
    return fallbackMessage;
  }

  return text;
}

// ── Small shared components ───────────────────────────────────────────────────

function ProgressBar({ step }: { step: number }) {
  return (
    <div className="flex gap-1.5">
      {STEPS.map((_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            background: i <= step ? "var(--accent)" : "var(--border)",
            transition: "background 0.2s",
          }}
        />
      ))}
    </div>
  );
}

const inputCls = "w-full rounded-xl px-4 text-base outline-none";
const inputStyle: React.CSSProperties = {
  border: "1.5px solid var(--border)",
  color: "var(--foreground)",
  background: "white",
  height: 52,
};

function BigInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={inputCls}
      style={inputStyle}
      {...props}
    />
  );
}

function PrimaryButton({
  children,
  loading,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled ?? loading}
      className="flex w-full items-center justify-center gap-2 rounded-xl py-4 text-base font-bold transition-opacity disabled:opacity-50"
      style={{ background: "var(--accent)", color: "white" }}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
  danger,
  loading,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="flex w-full items-center justify-center gap-2 rounded-xl py-4 text-base font-semibold transition-colors disabled:opacity-50"
      style={{
        border: `1.5px solid ${danger ? "#fee2e2" : "var(--border)"}`,
        color: danger ? "#b91c1c" : "var(--foreground)",
        background: danger ? "#fef2f2" : "white",
      }}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

function OptionButtons({
  label,
  options,
  value,
  onChange,
  required,
}: {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
        {label}
        {required && <span style={{ color: "var(--accent)" }}> *</span>}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors"
              style={{
                border: `1.5px solid ${active ? "var(--accent)" : "var(--border)"}`,
                background: active ? "var(--accent)" : "white",
                color: active ? "white" : "var(--foreground)",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeader({ number, title }: { number: number; title: string }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
        style={{ background: "var(--accent)" }}
      >
        {number}
      </div>
      <h2
        className="text-lg font-black"
        style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
      >
        {title}
      </h2>
    </div>
  );
}

// ── Step 1 — Vehicle lookup ───────────────────────────────────────────────────

function StepVehicle({
  regNumber,
  setRegNumber,
  vehicle,
  setVehicle,
  onNext,
  saving,
  hideNav,
}: {
  regNumber: string;
  setRegNumber: (v: string) => void;
  vehicle: LookedUpVehicle | null;
  setVehicle: (v: LookedUpVehicle | null) => void;
  onNext: () => void;
  saving: boolean;
  hideNav?: boolean;
}) {
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function doLookup() {
    const reg = regNumber.trim().toUpperCase().replace(/\s/g, "");
    if (!reg) return;
    setLooking(true);
    setLookupError(null);
    setVehicle(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/vehicles/lookup?reg=${encodeURIComponent(reg)}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        setLookupError((data as { error?: string }).error ?? "Ukjent feil");
      } else {
        setVehicle(data as LookedUpVehicle);
      }
    } catch {
      setLookupError("Kunne ikke nå Vegvesenet. Sjekk nettforbindelsen.");
    } finally {
      setLooking(false);
    }
  }

  const formatted = regNumber.toUpperCase().replace(/\s/g, "");
  const isValidFormat = /^[A-Z]{2}\d{5}$/.test(formatted);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
          Registreringsnummer
        </p>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            className="flex-1 rounded-xl px-4 text-xl font-bold tracking-widest uppercase outline-none"
            style={{ ...inputStyle, letterSpacing: "0.15em" }}
            value={regNumber}
            onChange={(e) => {
              setRegNumber(e.target.value.toUpperCase());
              setVehicle(null);
              setLookupError(null);
            }}
            onKeyDown={(e) => { if (e.key === "Enter" && isValidFormat) void doLookup(); }}
            placeholder="AB12345"
            maxLength={8}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            onClick={() => void doLookup()}
            disabled={!isValidFormat || looking}
            className="flex h-[52px] w-[52px] flex-shrink-0 items-center justify-center rounded-xl transition-opacity disabled:opacity-40"
            style={{ background: "var(--accent)", color: "white" }}
          >
            {looking ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Search className="h-5 w-5" />
            )}
          </button>
        </div>
        {lookupError && (
          <p className="mt-2 text-sm font-medium" style={{ color: "#b91c1c" }}>
            {lookupError}
          </p>
        )}
      </div>

      {vehicle && (
        <div
          className="overflow-hidden rounded-xl"
          style={{ border: "2px solid #16a34a", background: "#f0fdf4" }}
        >
          <div
            className="flex items-center gap-2 px-4 py-2.5"
            style={{ background: "#16a34a" }}
          >
            <CheckCircle className="h-4 w-4 text-white" />
            <span className="text-sm font-bold text-white">Kjøretøy funnet</span>
          </div>
          <div className="px-4 py-4">
            <p
              className="text-xl font-black"
              style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
            >
              {vehicle.make} {vehicle.model}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                vehicle.modelYear ? String(vehicle.modelYear) : null,
                vehicle.fuelType ? normalizeFuelType(vehicle.fuelType) : null,
                vehicle.transmission ? normalizeTransmission(vehicle.transmission) : null,
                vehicle.bodyType,
                vehicle.exteriorColor,
              ]
                .filter(Boolean)
                .map((tag, i) => (
                  <span
                    key={i}
                    className="rounded-full px-3 py-1 text-sm font-medium"
                    style={{ background: "white", color: "var(--foreground)", border: "1px solid #bbf7d0" }}
                  >
                    {tag}
                  </span>
                ))}
            </div>
            {vehicle.vin && (
              <p className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                VIN: {vehicle.vin}
              </p>
            )}
          </div>
        </div>
      )}

      {!hideNav && (
        <PrimaryButton onClick={onNext} loading={saving} disabled={!vehicle}>
          Neste <ChevronRight className="h-4 w-4" />
        </PrimaryButton>
      )}
    </div>
  );
}

// ── Step 2 — Mileage ─────────────────────────────────────────────────────────

function StepMileage({
  mileage,
  setMileage,
  onNext,
  onBack,
  saving,
  hideNav,
}: {
  mileage: string;
  setMileage: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  hideNav?: boolean;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
          Kilometerstand
        </p>
        <div className="relative">
          <BigInput
            type="text"
            inputMode="numeric"
            value={mileage ? Number(mileage).toLocaleString("nb-NO") : ""}
            onChange={(e) => setMileage(e.target.value.replace(/\s/g, "").replace(/[^0-9]/g, ""))}
            placeholder="45 000"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && mileage) onNext(); }}
          />
          <span
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-base font-medium"
            style={{ color: "var(--muted-foreground)" }}
          >
            km
          </span>
        </div>
      </div>
      {!hideNav && (
        <>
          <PrimaryButton onClick={onNext} loading={saving} disabled={!mileage}>
            Neste <ChevronRight className="h-4 w-4" />
          </PrimaryButton>
          <SecondaryButton onClick={onBack}>Tilbake</SecondaryButton>
        </>
      )}
    </div>
  );
}

// ── Step 3 — Photos ───────────────────────────────────────────────────────────

function StepPhotos({
  images,
  setImages,
  organizationSlug,
  onNext,
  onBack,
  saving,
  hideNav,
}: {
  images: ImageRow[];
  setImages: React.Dispatch<React.SetStateAction<ImageRow[]>>;
  organizationSlug: string;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  hideNav?: boolean;
}) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  // When true, re-open camera automatically after each capture
  const [shootingMode, setShootingMode] = useState(false);
  const shootingModeRef = useRef(false);
  shootingModeRef.current = shootingMode;
  const [isDragging, setIsDragging] = useState(false);
  const uploadedCount = images.filter((img) => img.url && !img.uploading).length;
  const anyUploading = images.some((img) => img.uploading);

  async function uploadFile(file: File, index: number) {
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const form = new FormData();
    form.append("file", file);

    const res = await fetch(`${api}/api/vehicles/images`, {
      method: "POST",
      credentials: "include",
      body: form,
    });

    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { error: data.error ?? "Opplasting feilet" };
    }

    const data = (await res.json()) as { url: string };
    return { url: data.url };
  }

  async function processFile(file: File): Promise<File> {
    const MAX_BYTES = 12 * 1024 * 1024;

    let working = file;

    // Convert HEIC/HEIF to JPEG
    const isHeic =
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      /\.heic$/i.test(file.name);

    if (isHeic) {
      const { default: heic2any } = await import("heic2any");
      const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const result = Array.isArray(blob) ? blob[0] : blob;
      working = new File([result], file.name.replace(/\.heic$/i, ".jpg"), {
        type: "image/jpeg",
      });
    }

    // Compress if still too large
    if (working.size > MAX_BYTES) {
      working = await compressImage(working, 0.82);
    }

    return working;
  }

  function compressImage(file: File, quality: number): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX_DIM = 3000;
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width >= height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error("Komprimering feilet")); return; }
            const name = file.name.replace(/\.[^.]+$/, ".jpg");
            resolve(new File([blob], name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality,
        );
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  async function handleFiles(files: FileList | null, fromCamera = false) {
    if (!files || files.length === 0) {
      // User cancelled camera — exit shooting mode
      if (fromCamera) setShootingMode(false);
      return;
    }

    const fileArray = Array.from(files);
    const startIndex = images.length;

    // Add placeholder rows immediately so the grid updates right away
    setImages((prev) => [
      ...prev,
      ...fileArray.map(() => ({ url: "", uploading: true })),
    ]);

    // Reset camera input so the same file can be re-selected
    if (fromCamera) {
      const input = cameraInputRef.current;
      if (input) input.value = "";
    }

    await Promise.all(
      fileArray.map(async (file, i) => {
        const idx = startIndex + i;
        try {
          const processed = await processFile(file);
          const localPreview = URL.createObjectURL(processed);

          // Show preview as soon as the file is ready, before upload completes
          setImages((prev) => {
            const next = [...prev];
            if (next[idx]) next[idx] = { ...next[idx], localPreview };
            return next;
          });

          const result = await uploadFile(processed, idx);
          const uploadedUrl = "url" in result ? (result as { url: string }).url : "";
          const uploadError = "error" in result ? (result as { error: string }).error : undefined;
          setImages((prev) => {
            const next = [...prev];
            const row = next[idx];
            if (!row) return next;
            next[idx] = {
              url: uploadedUrl,
              localPreview: row.localPreview,
              uploading: false,
              ...(uploadError ? { error: uploadError } : {}),
            };
            return next;
          });
        } catch {
          setImages((prev) => {
            const next = [...prev];
            if (next[idx]) next[idx] = { url: "", uploading: false, error: "Behandling av bilde feilet" };
            return next;
          });
        }
      }),
    );
  }

  function startShootingMode() {
    setShootingMode(true);
    const input = cameraInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
  }

  function stopShootingMode() {
    setShootingMode(false);
  }

  function removeImage(index: number) {
    const img = images[index];
    if (img.localPreview) URL.revokeObjectURL(img.localPreview);
    setImages(images.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
        Det første bildet brukes som hovedbilde
      </p>

      {/* ── Desktop/laptop (pointer: fine): drop-zone with drag support ── */}
      <button
        onClick={() => galleryInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className="flex w-full flex-col items-center justify-center gap-3 rounded-xl py-8 transition-colors touch:hidden"
        style={{
          border: `2px dashed ${isDragging ? "var(--accent)" : "var(--border)"}`,
          background: isDragging ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "var(--muted)",
        }}
      >
        <div
          className="flex h-12 w-12 items-center justify-center rounded-full"
          style={{ background: "white" }}
        >
          <Images className="h-6 w-6" style={{ color: "var(--accent)" }} />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold" style={{ color: "var(--foreground)" }}>
            {isDragging ? "Slipp bildet her" : "Dra og slipp, eller klikk for å velge"}
          </p>
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            JPEG, PNG, WebP, HEIC
          </p>
        </div>
      </button>

      {/* ── Touch devices — phones + tablets (pointer: coarse) ── */}
      <div className="hidden touch:block">
        {/* Shooting mode banner */}
        {shootingMode && (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { const input = cameraInputRef.current; if (input) { input.value = ""; input.click(); } }}
              className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-4 text-white"
              style={{ background: "var(--accent)" }}
            >
              <Camera className="h-5 w-5" />
              <span className="text-sm font-semibold">Ta neste bilde</span>
            </button>
            <button
              onClick={stopShootingMode}
              className="rounded-xl px-4 py-2 text-xs font-bold"
              style={{ background: "var(--muted)", color: "var(--muted-foreground)" }}
            >
              Ferdig
            </button>
          </div>
        )}

        {!shootingMode && (
          <div className="grid grid-cols-2 gap-3">
            {/* Camera button — opens camera directly, loops for multiple shots */}
            <button
              onClick={startShootingMode}
              className="flex flex-col items-center justify-center gap-2 rounded-xl py-6 transition-colors"
              style={{ border: "2px solid var(--accent)", background: "var(--accent)" }}
            >
              <Camera className="h-7 w-7 text-white" />
              <div className="text-center">
                <p className="text-sm font-semibold text-white">Ta bilder</p>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
                  Kamera · ett om gangen
                </p>
              </div>
            </button>

            {/* Gallery button — native multi-select from photo library */}
            <button
              onClick={() => { galleryInputRef.current?.click(); }}
              className="flex flex-col items-center justify-center gap-2 rounded-xl py-6 transition-colors"
              style={{ border: "2px dashed var(--border)", background: "var(--muted)" }}
            >
              <Images className="h-7 w-7" style={{ color: "var(--accent)" }} />
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: "var(--foreground)" }}>
                  Velg fra galleri
                </p>
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  Velg flere på én gang
                </p>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Hidden camera input — no `multiple`, forces camera on mobile */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        capture="environment"
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files, true)}
      />

      {/* Hidden gallery input — multi-select, no capture attribute */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
        onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
      />

      {/* Uploaded image grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((img, i) => {
            const preview = img.localPreview ?? img.url;
            return (
              <div
                key={i}
                className="relative overflow-hidden rounded-xl"
                style={{
                  aspectRatio: "1",
                  background: "var(--muted)",
                  outline: i === 0 ? "2px solid var(--accent)" : "none",
                  outlineOffset: 2,
                }}
              >
                {preview && (
                  <img
                    src={preview}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}

                {/* Uploading overlay */}
                {img.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }}>
                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                  </div>
                )}

                {/* Error overlay */}
                {img.error && !img.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(185,28,28,0.7)" }}>
                    <p className="px-1 text-center text-xs font-bold text-white">Feil</p>
                  </div>
                )}

                {/* Primary badge */}
                {i === 0 && !img.uploading && img.url && (
                  <div
                    className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-xs font-bold text-white"
                    style={{ background: "var(--accent)" }}
                  >
                    1
                  </div>
                )}

                {/* Remove button */}
                {!img.uploading && (
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full"
                    style={{ background: "rgba(0,0,0,0.55)" }}
                  >
                    <X className="h-3.5 w-3.5 text-white" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs" style={{ color: uploadedCount < 4 ? "var(--destructive, #ef4444)" : "var(--muted-foreground)" }}>
        {uploadedCount} av minimum 4 bilder lastet opp
        {anyUploading ? " · laster opp…" : ""}
      </p>

      {!hideNav && (
        <>
          <PrimaryButton onClick={onNext} loading={saving} disabled={uploadedCount < 4 || anyUploading}>
            Neste <ChevronRight className="h-4 w-4" />
          </PrimaryButton>
          <SecondaryButton onClick={onBack}>Tilbake</SecondaryButton>
        </>
      )}
    </div>
  );
}

// ── Step 4 — Price & timing ───────────────────────────────────────────────────

// ── Step 4 — Price & details ─────────────────────────────────────────────────

function StepDetails({
  startingBid,
  setStartingBid,
  description,
  setDescription,
  numberOfKeys,
  setNumberOfKeys,
  numberOfWheelSets,
  setNumberOfWheelSets,
  tireCondition,
  setTireCondition,
  serviceHistory,
  setServiceHistory,
  hasConditionReport,
  setHasConditionReport,
  conditionReport,
  onUploadConditionReport,
  onRemoveConditionReport,
  onNext,
  onBack,
  saving,
  hideNav,
}: {
  startingBid: string;
  setStartingBid: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  numberOfKeys: string;
  setNumberOfKeys: (v: string) => void;
  numberOfWheelSets: string;
  setNumberOfWheelSets: (v: string) => void;
  tireCondition: string;
  setTireCondition: (v: string) => void;
  serviceHistory: string;
  setServiceHistory: (v: string) => void;
  hasConditionReport: string;
  setHasConditionReport: (v: string) => void;
  conditionReport: ConditionReport | null;
  onUploadConditionReport: (file: File) => void;
  onRemoveConditionReport: () => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  hideNav?: boolean;
}) {
  const reportInputRef = useRef<HTMLInputElement>(null);
  const conditionReportOk =
    hasConditionReport === "nei" || (hasConditionReport === "ja" && !!conditionReport?.id);
  const canContinue = !!(startingBid && description.trim() && tireCondition && serviceHistory && hasConditionReport && conditionReportOk);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
          Startpris (kr) <span style={{ color: "var(--accent)" }}>*</span>
        </p>
        <BigInput
          type="text"
          inputMode="numeric"
          value={startingBid ? Number(startingBid).toLocaleString("nb-NO") : ""}
          onChange={(e) => setStartingBid(e.target.value.replace(/\s/g, "").replace(/[^0-9]/g, ""))}
          placeholder="0"
        />
      </div>

      <div>
        <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
          Beskrivelse <span style={{ color: "var(--accent)" }}>*</span>
        </p>
        <textarea
          className="w-full rounded-xl px-4 py-3 text-base outline-none"
          style={{
            border: "1.5px solid var(--border)",
            color: "var(--foreground)",
            background: "white",
            resize: "vertical",
            minHeight: 96,
          }}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Beskriv bilens tilstand, utstyr og annen relevant informasjon..."
          rows={3}
        />
      </div>

      <OptionButtons
        label="Antall nøkler"
        options={[
          { label: "1", value: "1" },
          { label: "2", value: "2" },
          { label: "3+", value: "3+" },
          { label: "Vet ikke", value: "vet_ikke" },
        ]}
        value={numberOfKeys}
        onChange={setNumberOfKeys}
      />

      <OptionButtons
        label="Antall hjulsett"
        options={[
          { label: "1", value: "1" },
          { label: "2", value: "2" },
          { label: "Vet ikke", value: "vet_ikke" },
        ]}
        value={numberOfWheelSets}
        onChange={setNumberOfWheelSets}
      />

      <OptionButtons
        label="Tilstand på dekk"
        options={[
          { label: "Slitt", value: "slitt" },
          { label: "OK tilstand", value: "ok" },
          { label: "Nye", value: "ny" },
          { label: "Vet ikke", value: "vet_ikke" },
        ]}
        value={tireCondition}
        onChange={setTireCondition}
        required
      />

      <OptionButtons
        label="Servicehistorikk"
        options={[
          { label: "Ja", value: "ja" },
          { label: "Nei", value: "nei" },
          { label: "Vet ikke", value: "vet_ikke" },
        ]}
        value={serviceHistory}
        onChange={setServiceHistory}
        required
      />

      <OptionButtons
        label="Har du tilstandsrapport?"
        options={[
          { label: "Ja", value: "ja" },
          { label: "Nei", value: "nei" },
        ]}
        value={hasConditionReport}
        onChange={setHasConditionReport}
        required
      />

      {hasConditionReport === "ja" && (
        <div>
          <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
            Tilstandsrapport (PDF)
          </p>
          {conditionReport ? (
            <div
              className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
              style={{ border: "1.5px solid var(--border)", background: "white" }}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                {conditionReport.uploading && (
                  <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" style={{ color: "var(--muted-foreground)" }} />
                )}
                {!conditionReport.uploading && !conditionReport.error && (
                  <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: "#16a34a" }} />
                )}
                {conditionReport.error && (
                  <X className="h-4 w-4 flex-shrink-0" style={{ color: "#b91c1c" }} />
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold" style={{ color: "var(--foreground)" }}>
                    {conditionReport.fileName || "Tilstandsrapport.pdf"}
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: conditionReport.error ? "#b91c1c" : "var(--muted-foreground)" }}
                  >
                    {conditionReport.uploading
                      ? "Laster opp..."
                      : conditionReport.error || "Lastet opp"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onRemoveConditionReport}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                style={{ border: "1.5px solid var(--border)", color: "var(--muted-foreground)" }}
                aria-label="Fjern tilstandsrapport"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <input
                ref={reportInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUploadConditionReport(file);
                  e.target.value = "";
                }}
              />
              <SecondaryButton onClick={() => reportInputRef.current?.click()}>
                <Plus className="h-4 w-4" /> Last opp PDF
              </SecondaryButton>
            </>
          )}
        </div>
      )}

      {!hideNav && (
        <>
          <PrimaryButton onClick={onNext} loading={saving} disabled={!canContinue}>
            Neste <ChevronRight className="h-4 w-4" />
          </PrimaryButton>
          <SecondaryButton onClick={onBack}>Tilbake</SecondaryButton>
        </>
      )}
    </div>
  );
}

// ── Step 5 — Location ─────────────────────────────────────────────────────────

function StepLocation({
  pickupAddress,
  setPickupAddress,
  pickupPostalCode,
  setPickupPostalCode,
  pickupCity,
  setPickupCity,
  shippingAddresses,
  pickupSource,
  selectedShippingAddressIndex,
  onSelectShippingAddress,
  onSelectCustomAddress,
  onNext,
  onBack,
  saving,
  hideNav,
}: {
  pickupAddress: string;
  setPickupAddress: (v: string) => void;
  pickupPostalCode: string;
  setPickupPostalCode: (v: string) => void;
  pickupCity: string;
  setPickupCity: (v: string) => void;
  shippingAddresses: ShippingAddress[];
  pickupSource: "shipping" | "custom";
  selectedShippingAddressIndex: number;
  onSelectShippingAddress: (index: number) => void;
  onSelectCustomAddress: () => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  hideNav?: boolean;
}) {
  const canContinue = !!(pickupAddress && pickupPostalCode && pickupCity);

  return (
    <div className="flex flex-col gap-5">
      {shippingAddresses.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
            Velg hvilken adresse bilen står på
          </p>
          <div className="grid gap-3">
            {shippingAddresses.map((shippingAddress, index) => {
              const isSelected = pickupSource === "shipping" && selectedShippingAddressIndex === index;
              const label = shippingAddress.label?.trim() || `Adresse ${index + 1}`;

              return (
                <button
                  key={`${label}-${shippingAddress.address}-${shippingAddress.postalCode}-${shippingAddress.city}`}
                  type="button"
                  onClick={() => onSelectShippingAddress(index)}
                  className="rounded-xl border px-4 py-3 text-left transition-colors"
                  style={{
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    background: isSelected ? "color-mix(in srgb, var(--accent) 8%, white)" : "white",
                  }}
                >
                  <p className="text-sm font-bold" style={{ color: "var(--foreground)" }}>{label}</p>
                  <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
                    {[shippingAddress.address, shippingAddress.postalCode, shippingAddress.city].filter(Boolean).join(", ")}
                  </p>
                </button>
              );
            })}

            <button
              type="button"
              onClick={onSelectCustomAddress}
              className="rounded-xl border px-4 py-3 text-left transition-colors"
              style={{
                borderColor: pickupSource === "custom" ? "var(--accent)" : "var(--border)",
                background: pickupSource === "custom" ? "color-mix(in srgb, var(--accent) 8%, white)" : "white",
              }}
            >
              <p className="text-sm font-bold" style={{ color: "var(--foreground)" }}>Tilpasset adresse</p>
              <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
                Bruk en annen adresse hvis bilen ikke står på en lagret leveringsadresse.
              </p>
            </button>
          </div>
        </div>
      )}

      {pickupSource === "custom" && (
        <>
          <div>
            <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
              Adresse
            </p>
            <BigInput
              type="text"
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              placeholder="Storgata 1"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
                Postnummer
              </p>
              <BigInput
                type="text"
                value={pickupPostalCode}
                onChange={(e) => setPickupPostalCode(e.target.value)}
                placeholder="0179"
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
                By
              </p>
              <BigInput
                type="text"
                value={pickupCity}
                onChange={(e) => setPickupCity(e.target.value)}
                placeholder="Oslo"
              />
            </div>
          </div>
        </>
      )}

      {!hideNav && (
        <>
          <PrimaryButton onClick={onNext} loading={saving} disabled={!canContinue}>
            Neste <ChevronRight className="h-4 w-4" />
          </PrimaryButton>
          <SecondaryButton onClick={onBack}>Tilbake</SecondaryButton>
        </>
      )}
    </div>
  );
}

// ── Step 6 — Auction Type ─────────────────────────────────────────────────────

function StepAuctionType({
  auctionType,
  setAuctionType,
  durationMinutes,
  setDurationMinutes,
  onSaveDraft,
  onDelete,
  onSubmit,
  onBack,
  saving,
  isEdit,
  canEdit,
  hideNav,
}: {
  auctionType: "regular" | "flash";
  setAuctionType: (v: "regular" | "flash") => void;
  durationMinutes: number;
  setDurationMinutes: (v: number) => void;
  onSaveDraft: () => void;
  onDelete?: () => void;
  onSubmit: () => void;
  onBack: () => void;
  saving: boolean;
  isEdit?: boolean;
  canEdit?: boolean;
  hideNav?: boolean;
}) {
  const flashOptions = [
    { label: "15 min", value: 15 },
    { label: "1 time", value: 60 },
    { label: "2 timer", value: 120 },
  ];

  const regularOptions = [
    { label: "24 timer", value: 1440 },
    { label: "48 timer", value: 2880 },
    { label: "72 timer", value: 4320 },
  ];

  return (
    <div className="flex flex-col gap-6">
      <OptionButtons
        label="Auksjonstype"
        options={[
          { label: "Lynauksjon", value: "flash" },
          { label: "Vanlig auksjon", value: "regular" },
        ]}
        value={auctionType}
        onChange={(v) => {
          setAuctionType(v as "regular" | "flash");
          setDurationMinutes(v === "flash" ? 60 : 1440);
        }}
        required
      />

      <div>
        <p className="mb-3 text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
          Varighet
        </p>
        <div className="flex flex-wrap gap-2">
          {(auctionType === "flash" ? flashOptions : regularOptions).map((opt) => {
             const active = durationMinutes === opt.value;
             return (
               <button
                 key={opt.value}
                 type="button"
                 onClick={() => setDurationMinutes(opt.value)}
                 className="rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors"
                 style={{
                   border: `1.5px solid ${active ? "var(--accent)" : "var(--border)"}`,
                   background: active ? "var(--accent)" : "white",
                   color: active ? "white" : "var(--foreground)",
                 }}
               >
                 {opt.label}
               </button>
             );
          })}
        </div>
      </div>

      {!hideNav && (
        <div className="flex flex-col gap-3 pt-2">
          <PrimaryButton onClick={onSubmit} loading={saving}>
            Send til godkjenning
          </PrimaryButton>
          <SecondaryButton onClick={onSaveDraft}>
            {saving ? "Lagrer..." : "Lagre utkast"}
          </SecondaryButton>
          {isEdit && canEdit && onDelete && (
            <SecondaryButton onClick={onDelete} danger loading={saving}>
              <Trash2 className="h-4 w-4" /> Slett utkast
            </SecondaryButton>
          )}
          <SecondaryButton onClick={onBack}>Tilbake</SecondaryButton>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AuctionFormPage() {
  const { organizationSlug, auctionId } = useParams<{
    organizationSlug: string;
    auctionId?: string;
  }>();
  const navigate = useNavigate();
  const isEdit = Boolean(auctionId);
  const isDesktop = useIsDesktop();
  const useStacked = isDesktop || isEdit;

  const [step, setStep] = useState(0);

  // Step 1 state
  const [regNumber, setRegNumber] = useState("");
  const [vehicle, setVehicle] = useState<LookedUpVehicle | null>(null);

  // Step 2 state
  const [mileage, setMileage] = useState("");

  // Step 3 state
  const [images, setImages] = useState<ImageRow[]>([]);

  // Step 4 state
  const [startingBid, setStartingBid] = useState("0");
  const [description, setDescription] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupPostalCode, setPickupPostalCode] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [shippingAddresses, setShippingAddresses] = useState<ShippingAddress[]>([]);
  const [pickupSource, setPickupSource] = useState<"shipping" | "custom">("custom");
  const [selectedShippingAddressIndex, setSelectedShippingAddressIndex] = useState(0);
  const [numberOfKeys, setNumberOfKeys] = useState("");
  const [numberOfWheelSets, setNumberOfWheelSets] = useState("");
  const [tireCondition, setTireCondition] = useState("");
  const [serviceHistory, setServiceHistory] = useState("");
  const [hasConditionReport, setHasConditionReport] = useState("");
  const [conditionReport, setConditionReport] = useState<ConditionReport | null>(null);
  // Guards against a slow upload response overwriting a newer upload/removal
  const conditionReportUploadSeq = useRef(0);
  const [auctionType, setAuctionType] = useState<"regular" | "flash">("regular");
  const [durationMinutes, setDurationMinutes] = useState<number>(1440);

  const [savedAuctionId, setSavedAuctionId] = useState<string | null>(auctionId ?? null);
  const [reviewStatus, setReviewStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyShippingAddress = useCallback((index: number, addresses: ShippingAddress[] = shippingAddresses) => {
    const shippingAddress = addresses[index];
    if (!shippingAddress) return;

    setSelectedShippingAddressIndex(index);
    setPickupSource("shipping");
    setPickupAddress(shippingAddress.address ?? "");
    setPickupPostalCode(shippingAddress.postalCode ?? "");
    setPickupCity(shippingAddress.city ?? "");
  }, [shippingAddresses]);

  // Load existing auction when editing
  const loadExisting = useCallback(async () => {
    if (!auctionId || !organizationSlug) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    try {
      const res = await fetch(
        `${api}/api/organizations/${organizationSlug}/auctions/${auctionId}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Fant ikke auksjonen");
      const data = await res.json();

      const v = data.vehicle;
      if (v) {
        setRegNumber(v.registrationNumber ?? "");
        setMileage(v.mileage != null ? String(v.mileage) : "");
        setVehicle({
          registrationNumber: v.registrationNumber ?? "",
          vin: v.vin ?? null,
          make: v.make ?? "",
          model: v.model ?? "",
          modelYear: v.modelYear ?? null,
          fuelType: v.fuelType ?? null,
          transmission: v.transmission ?? null,
          wheelDrive: v.wheelDrive ?? null,
          bodyType: v.bodyType ?? null,
          exteriorColor: v.exteriorColor ?? null,
          registrationDate: null,
          nextEuControl: null,
        });
        const imgs = ([...(v.images ?? [])] as { url: string; sortOrder: number }[])
          .sort((a, b) => a.sortOrder - b.sortOrder);
        if (imgs.length > 0) {
          setImages(imgs.map((img) => ({ url: img.url })));
        }
      }

      setStartingBid(data.startingBid != null ? String(data.startingBid) : "0");
      setDescription(data.description ?? "");
      setPickupAddress(data.pickupAddress ?? "");
      setPickupPostalCode(data.pickupPostalCode ?? "");
      setPickupCity(data.pickupCity ?? "");
      setPickupSource("custom");
      setReviewStatus(data.reviewStatus);
      setAuctionType(data.type ?? "regular");
      setDurationMinutes(data.durationMinutes ?? 1440);

      const td = (v?.technicalData ?? {}) as Record<string, string>;
      setNumberOfKeys(td.numberOfKeys ?? "");
      setNumberOfWheelSets(td.numberOfWheelSets ?? "");
      setTireCondition(td.tireCondition ?? "");
      setServiceHistory(td.serviceHistory ?? "");
      setHasConditionReport(td.hasConditionReport ?? "");

      const documents = (v?.documents ?? []) as {
        id: string;
        type: string;
        fileName?: string | null;
      }[];
      const existingReport = documents.find((doc) => doc.type === "condition_report");
      if (existingReport) {
        setConditionReport({ id: existingReport.id, fileName: existingReport.fileName ?? null });
        if (!td.hasConditionReport) setHasConditionReport("ja");
      }
      setSavedAuctionId(auctionId);

      // Jump to last incomplete step
      if (v?.mileage == null) setStep(1);
      else if ((v.images ?? []).length === 0) setStep(2);
      else if (!data.description || !data.startingBid) setStep(3);
      else if (!data.pickupAddress) setStep(4);
      else setStep(5);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt");
    } finally {
      setLoading(false);
    }
  }, [auctionId, organizationSlug]);

  const loadOrganization = useCallback(async () => {
    if (!organizationSlug) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";

    try {
      const res = await fetch(`${api}/api/organizations/${organizationSlug}`, {
        credentials: "include",
      });
      if (!res.ok) return;

      const data = await res.json() as OrganizationDetail;
      const addresses = data.shippingAddresses ?? [];
      if (addresses.length > 0) {
        setShippingAddresses(addresses);
      }
    } catch {
      // Ignore organization lookup failures and allow manual pickup address entry instead.
    }
  }, [organizationSlug]);

  useEffect(() => {
    if (isEdit) void loadExisting();
  }, [isEdit, loadExisting]);

  useEffect(() => {
    void loadOrganization();
  }, [loadOrganization]);

  // Auto-apply default address only once on new auction creation
  const [didAutoApply, setDidAutoApply] = useState(false);
  useEffect(() => {
    if (!isEdit && !didAutoApply && shippingAddresses.length > 0 && !pickupAddress && !pickupPostalCode && !pickupCity) {
      applyShippingAddress(0, shippingAddresses);
      setDidAutoApply(true);
    }
  }, [isEdit, didAutoApply, shippingAddresses, pickupAddress, pickupPostalCode, pickupCity, applyShippingAddress]);

  useEffect(() => {
    if (pickupSource === "custom" || shippingAddresses.length === 0 || !pickupAddress || !pickupPostalCode || !pickupCity) {
      return;
    }

    const matchedIndex = shippingAddresses.findIndex((shippingAddress) =>
      shippingAddress.address === pickupAddress &&
      shippingAddress.postalCode === pickupPostalCode &&
      shippingAddress.city === pickupCity
    );

    if (matchedIndex >= 0) {
      setSelectedShippingAddressIndex(matchedIndex);
      setPickupSource("shipping");
    }
  }, [shippingAddresses, pickupAddress, pickupPostalCode, pickupCity, pickupSource]);

  function removeConditionReport() {
    conditionReportUploadSeq.current += 1;
    setConditionReport(null);
  }

  async function uploadConditionReport(file: File) {
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const seq = ++conditionReportUploadSeq.current;
    setConditionReport({ id: "", fileName: file.name, uploading: true });

    try {
      const form = new FormData();
      form.append("file", file);
      // Known identifiers help the background redaction find every occurrence
      if (vehicle?.vin) form.append("vin", vehicle.vin);
      const reg = regNumber.trim().toUpperCase().replace(/\s/g, "");
      if (reg) form.append("registrationNumber", reg);

      const res = await fetch(`${api}/api/vehicles/documents`, {
        method: "POST",
        credentials: "include",
        body: form,
      });

      if (seq !== conditionReportUploadSeq.current) return; // superseded or removed

      if (!res.ok) {
        const message = await readApiError(res, "Opplastingen feilet. Prøv igjen.");
        setConditionReport({ id: "", fileName: file.name, error: message });
        return;
      }

      const data = (await res.json()) as { id: string };
      if (seq !== conditionReportUploadSeq.current) return;
      setConditionReport({ id: data.id, fileName: file.name });
    } catch {
      if (seq !== conditionReportUploadSeq.current) return;
      setConditionReport({ id: "", fileName: file.name, error: "Opplastingen feilet. Prøv igjen." });
    }
  }

  function buildPayload(andSubmit = false) {
    const validImages = images.filter((img) => img.url.trim() && !img.uploading && !img.error);
    const technicalData: Record<string, string> = {};
    if (numberOfKeys) technicalData.numberOfKeys = numberOfKeys;
    if (numberOfWheelSets) technicalData.numberOfWheelSets = numberOfWheelSets;
    if (tireCondition) technicalData.tireCondition = tireCondition;
    if (serviceHistory) technicalData.serviceHistory = serviceHistory;
    if (hasConditionReport) technicalData.hasConditionReport = hasConditionReport;

    return {
      vehicle: {
        ...(vehicle
          ? {
              make: vehicle.make,
              model: vehicle.model,
              variant: null,
              registrationNumber: regNumber.trim().toUpperCase().replace(/\s/g, "") || null,
              vin: vehicle.vin || null,
              modelYear: vehicle.modelYear ?? null,
              fuelType: vehicle.fuelType ?? null,
              transmission: vehicle.transmission ?? null,
              wheelDrive: vehicle.wheelDrive ?? null,
              bodyType: vehicle.bodyType ?? null,
              exteriorColor: vehicle.exteriorColor ?? null,
              registrationDate: vehicle.registrationDate
                ? new Date(vehicle.registrationDate).toISOString()
                : null,
              nextEuControl: vehicle.nextEuControl
                ? new Date(vehicle.nextEuControl).toISOString()
                : null,
            }
          : {}),
        mileage: mileage ? Number(mileage) : null,
        technicalData,
      },
      auction: {
        title: vehicle
          ? `${vehicle.make} ${vehicle.model}${vehicle.modelYear ? ` ${vehicle.modelYear}` : ""}${mileage ? ` – ${Number(mileage).toLocaleString("nb-NO")} km` : ""}`
          : "Ny auksjon",
        description: description.trim() || null,
        pickupAddress: pickupAddress.trim() || null,
        pickupPostalCode: pickupPostalCode.trim() || null,
        pickupCity: pickupCity.trim() || null,
        startingBid: Number(startingBid) || 0,
        minimumIncrement: 1000,
        startsAt: null,
        endsAt: null,
        type: auctionType,
        durationMinutes: durationMinutes || null,
      },
      images: validImages.map((img, i) => ({
        url: img.url.trim(),
        thumbnailUrl: null,
        altText: null,
        sortOrder: i,
        isPrimary: i === 0,
      })),
      // "nei" detaches; a completed upload links; otherwise omit the field so a
      // draft save mid-upload never detaches an already linked report
      conditionReportDocumentId:
        hasConditionReport === "nei"
          ? null
          : hasConditionReport === "ja" && conditionReport?.id
            ? conditionReport.id
            : undefined,
    };
  }

  async function saveProgress(andSubmit = false): Promise<string | null> {
    if (!organizationSlug) return null;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    setSaving(true);
    setError(null);

    try {
      const payload = buildPayload();
      let id = savedAuctionId;

      if (id) {
        const res = await fetch(
          `${api}/api/organizations/${organizationSlug}/auctions/${id}`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        if (!res.ok) {
          throw new Error(
            await readApiError(
              res,
              "Vi klarte ikke å lagre endringene akkurat nå. Prøv igjen om et lite øyeblikk.",
            ),
          );
        }
      } else {
        const res = await fetch(
          `${api}/api/organizations/${organizationSlug}/auctions`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        if (!res.ok) {
          throw new Error(
            await readApiError(
              res,
              "Auksjonen ble ikke opprettet denne gangen. Prøv igjen om et lite øyeblikk.",
            ),
          );
        }
        const data = (await res.json()) as { id: string };
        id = data.id;
        setSavedAuctionId(id);
      }

      if (andSubmit && id) {
        const res = await fetch(
          `${api}/api/organizations/${organizationSlug}/auctions/${id}/submit`,
          { method: "POST", credentials: "include" },
        );
        if (!res.ok) {
          throw new Error(
            await readApiError(
              res,
              "Vi fikk ikke sendt auksjonen til godkjenning akkurat nå. Prøv igjen om et lite øyeblikk.",
            ),
          );
        }
      }

      return id;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleStep1Next() {
    // Save draft and move to step 2 — URL updates to include auctionId so user can resume on PC
    const id = await saveProgress();
    if (id) {
      if (!isEdit) {
        navigate(`/${organizationSlug}/mine-auksjoner/${id}/rediger`, { replace: true });
      }
      setStep(1);
    }
  }

  async function handleStep2Next() {
    const id = await saveProgress();
    if (id) setStep(2);
  }

  async function handleStep3Next() {
    const id = await saveProgress();
    if (id) setStep(3);
  }

  async function handleStep4Next() {
    const id = await saveProgress();
    if (id) setStep(4);
  }

  async function handleStep5Next() {
    const id = await saveProgress();
    if (id) setStep(5);
  }

  async function handleSaveDraft() {
    const id = await saveProgress(false);
    if (id) navigate(`/${organizationSlug}/mine-auksjoner`);
  }

  async function handleSubmit() {
    const id = await saveProgress(true);
    if (id) navigate(`/${organizationSlug}/mine-auksjoner`);
  }

  async function handleDelete() {
    if (!savedAuctionId || !organizationSlug) return;
    if (!window.confirm("Er du sikker på at du vil slette dette utkastet? Dette kan ikke angres.")) {
      return;
    }

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(
        `${api}/api/organizations/${organizationSlug}/auctions/${savedAuctionId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      if (!res.ok) {
        throw new Error(
          await readApiError(
            res,
            "Vi klarte ikke å slette utkastet akkurat nå. Prøv igjen om et lite øyeblikk.",
          ),
        );
      }

      navigate(`/${organizationSlug}/mine-auksjoner`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt");
    } finally {
      setSaving(false);
    }
  }

  const canEdit = !reviewStatus || reviewStatus === "draft" || reviewStatus === "changes_requested";

  if (loading) {
    return (
      <div className="mx-auto flex max-w-lg flex-col gap-4 px-5 py-8">
        {[1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-xl" style={{ background: "var(--muted)" }} />
        ))}
      </div>
    );
  }

  const uploadedCount = images.filter((img) => img.url && !img.uploading).length;
  const conditionReportOk =
    hasConditionReport === "nei" || (hasConditionReport === "ja" && !!conditionReport?.id);
  const canSubmitStacked = !!(vehicle && mileage && uploadedCount >= 4 && startingBid && description.trim() && tireCondition && serviceHistory && hasConditionReport && conditionReportOk && pickupAddress && pickupPostalCode && pickupCity);

  const banners = (
    <>
      {!canEdit && (
        <div
          className="mb-5 rounded-xl px-4 py-3 text-sm font-medium"
          style={{ background: "#fef3c7", color: "#b45309" }}
        >
          Til gjennomgang – kan ikke redigeres.
        </div>
      )}
      {reviewStatus === "changes_requested" && (
        <div
          className="mb-5 rounded-xl px-4 py-3 text-sm font-medium"
          style={{ background: "#fee2e2", color: "#b91c1c" }}
        >
          Administrator har bedt om endringer før godkjenning.
        </div>
      )}
      {error && (
        <div
          className="mb-5 rounded-xl px-4 py-3 text-sm font-medium"
          style={{ background: "#fee2e2", color: "#b91c1c" }}
        >
          {error}
        </div>
      )}
    </>
  );

  if (useStacked) {
    return (
      <div className="mx-auto max-w-xl px-5 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
            style={{ border: "1.5px solid var(--border)", color: "var(--muted-foreground)" }}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center justify-between flex-1">
            <h1
              className="text-2xl font-black"
              style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
            >
              {isEdit ? "Rediger auksjon" : "Ny auksjon"}
            </h1>
            {savedAuctionId && (
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Utkast lagret
              </p>
            )}
          </div>
        </div>

        {banners}

        <div className="flex flex-col gap-8">
          {/* Step 1 */}
          <div>
            <SectionHeader number={1} title="Bil" />
            <StepVehicle
              regNumber={regNumber}
              setRegNumber={setRegNumber}
              vehicle={vehicle}
              setVehicle={setVehicle}
              onNext={() => void handleStep1Next()}
              saving={saving}
              hideNav
            />
          </div>

          <div style={{ borderTop: "1px solid var(--border)" }} />

          {/* Step 2 */}
          <div>
            <SectionHeader number={2} title="Kilometerstand" />
            <StepMileage
              mileage={mileage}
              setMileage={setMileage}
              onNext={() => {}}
              onBack={() => {}}
              saving={saving}
              hideNav
            />
          </div>

          <div style={{ borderTop: "1px solid var(--border)" }} />

          {/* Step 3 */}
          <div>
            <SectionHeader number={3} title="Bilder" />
            <StepPhotos
              images={images}
              setImages={setImages}
              organizationSlug={organizationSlug ?? ""}
              onNext={() => {}}
              onBack={() => {}}
              saving={saving}
              hideNav
            />
          </div>

          <div style={{ borderTop: "1px solid var(--border)" }} />

          {/* Step 4 */}
          <div>
            <SectionHeader number={4} title="Detaljer" />
            <StepDetails
              startingBid={startingBid}
              setStartingBid={setStartingBid}
              description={description}
              setDescription={setDescription}
              numberOfKeys={numberOfKeys}
              setNumberOfKeys={setNumberOfKeys}
              numberOfWheelSets={numberOfWheelSets}
              setNumberOfWheelSets={setNumberOfWheelSets}
              tireCondition={tireCondition}
              setTireCondition={setTireCondition}
              serviceHistory={serviceHistory}
              setServiceHistory={setServiceHistory}
              hasConditionReport={hasConditionReport}
              setHasConditionReport={setHasConditionReport}
              conditionReport={conditionReport}
              onUploadConditionReport={(file) => void uploadConditionReport(file)}
              onRemoveConditionReport={removeConditionReport}
              onNext={() => {}}
              onBack={() => {}}
              saving={saving}
              hideNav
            />
          </div>

          <div style={{ borderTop: "1px solid var(--border)" }} />

          {/* Step 5 */}
          <div>
            <SectionHeader number={5} title="Hentested" />
            <StepLocation
              pickupAddress={pickupAddress}
              setPickupAddress={setPickupAddress}
              pickupPostalCode={pickupPostalCode}
              setPickupPostalCode={setPickupPostalCode}
              pickupCity={pickupCity}
              setPickupCity={setPickupCity}
              shippingAddresses={shippingAddresses}
              pickupSource={pickupSource}
              selectedShippingAddressIndex={selectedShippingAddressIndex}
              onSelectShippingAddress={applyShippingAddress}
              onSelectCustomAddress={() => {
                setPickupSource("custom");
                setPickupAddress("");
                setPickupPostalCode("");
                setPickupCity("");
              }}
              onNext={() => {}}
              onBack={() => {}}
              saving={saving}
              hideNav
            />
          </div>

          <div style={{ borderTop: "1px solid var(--border)" }} />

          {/* Step 6 */}
          <div>
            <SectionHeader number={6} title="Auksjonstype" />
            <StepAuctionType
              auctionType={auctionType}
              setAuctionType={setAuctionType}
              durationMinutes={durationMinutes}
              setDurationMinutes={setDurationMinutes}
              onSaveDraft={() => {}}
              onSubmit={() => {}}
              onBack={() => {}}
              saving={saving}
              hideNav
            />
          </div>

          {/* Submit actions */}
          <div className="flex flex-col gap-3">
            <PrimaryButton onClick={() => void handleSubmit()} loading={saving} disabled={!canSubmitStacked}>
              Send til godkjenning
            </PrimaryButton>
            <SecondaryButton onClick={() => void handleSaveDraft()}>
              {saving ? "Lagrer..." : "Lagre utkast"}
            </SecondaryButton>
            {isEdit && canEdit && (
              <SecondaryButton onClick={() => void handleDelete()} danger loading={saving}>
                <Trash2 className="h-4 w-4" /> Slett utkast
              </SecondaryButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-5 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => (step > 0 ? setStep(step - 1) : navigate(-1))}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
          style={{ border: "1.5px solid var(--border)", color: "var(--muted-foreground)" }}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium" style={{ color: "var(--muted-foreground)" }}>
              Steg {step + 1} av {STEPS.length} · {STEPS[step]}
            </p>
            {savedAuctionId && (
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Utkast lagret
              </p>
            )}
          </div>
          <ProgressBar step={step} />
        </div>
      </div>

      {/* Title */}
      <h1
        className="mb-6 text-2xl font-black"
        style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
      >
        {isEdit ? "Rediger auksjon" : "Ny auksjon"}
      </h1>

      {banners}

      {/* Step content */}
      {step === 0 && (
        <StepVehicle
          regNumber={regNumber}
          setRegNumber={setRegNumber}
          vehicle={vehicle}
          setVehicle={setVehicle}
          onNext={() => void handleStep1Next()}
          saving={saving}
        />
      )}
      {step === 1 && (
        <StepMileage
          mileage={mileage}
          setMileage={setMileage}
          onNext={() => void handleStep2Next()}
          onBack={() => setStep(0)}
          saving={saving}
        />
      )}
      {step === 2 && (
        <StepPhotos
          images={images}
          setImages={setImages}
          organizationSlug={organizationSlug ?? ""}
          onNext={() => void handleStep3Next()}
          onBack={() => setStep(1)}
          saving={saving}
        />
      )}
      {step === 3 && (
        <StepDetails
          startingBid={startingBid}
          setStartingBid={setStartingBid}
          description={description}
          setDescription={setDescription}
          numberOfKeys={numberOfKeys}
          setNumberOfKeys={setNumberOfKeys}
          numberOfWheelSets={numberOfWheelSets}
          setNumberOfWheelSets={setNumberOfWheelSets}
          tireCondition={tireCondition}
          setTireCondition={setTireCondition}
          serviceHistory={serviceHistory}
          setServiceHistory={setServiceHistory}
          hasConditionReport={hasConditionReport}
          setHasConditionReport={setHasConditionReport}
          conditionReport={conditionReport}
          onUploadConditionReport={(file) => void uploadConditionReport(file)}
          onRemoveConditionReport={removeConditionReport}
          onNext={() => void handleStep4Next()}
          onBack={() => setStep(2)}
          saving={saving}
        />
      )}
      {step === 4 && (
        <StepLocation
          pickupAddress={pickupAddress}
          setPickupAddress={setPickupAddress}
          pickupPostalCode={pickupPostalCode}
          setPickupPostalCode={setPickupPostalCode}
          pickupCity={pickupCity}
          setPickupCity={setPickupCity}
          shippingAddresses={shippingAddresses}
          pickupSource={pickupSource}
          selectedShippingAddressIndex={selectedShippingAddressIndex}
          onSelectShippingAddress={applyShippingAddress}
          onSelectCustomAddress={() => {
            setPickupSource("custom");
            setPickupAddress("");
            setPickupPostalCode("");
            setPickupCity("");
          }}
          onNext={() => void handleStep5Next()}
          onBack={() => setStep(3)}
          saving={saving}
        />
      )}
      {step === 5 && (
        <StepAuctionType
          auctionType={auctionType}
          setAuctionType={setAuctionType}
          durationMinutes={durationMinutes}
          setDurationMinutes={setDurationMinutes}
          onSaveDraft={() => void handleSaveDraft()}
          onDelete={() => void handleDelete()}
          onSubmit={() => void handleSubmit()}
          onBack={() => setStep(4)}
          saving={saving}
          isEdit={isEdit}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
